/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment-timezone';
import {DateType, toTimeZone} from '@ion/core/src/utils/DateUtil';
import {info} from '../logger/logger';

/**
 * @private
 * @default BrowserTimeZone
 *
 * Defines the authenticated User's Time Zone. This value determines the
 * conversions which are to be made against the date/timestamps of all
 * entity Attributes. Defaults to the current Browser Time Zone.
 */
let _userTimeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || 'UTC';

/**
 * @private
 *
 * Defines the CTC Services' Time Zone.
 */
const _platformTimeZone = 'UTC';

/**
 * @function getUserTimeZone
 *
 * Returns the application-wide Time Zone as specified via a call to the
 * setUserTimeZone function.
 *
 * ```typescript
 *   import {getUserTimeZone} from '@ion/core/src/timezone/timezone';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   const timeZone: string = getUserTimeZone();
 *   info(`User/Application Time Zone: [${timeZone}]`);
 *   info(user.timeZone === timeZone); // true
 *
 * ```
 *
 * @returns {string} The authenticated User's Time Zone.
 */
export const getUserTimeZone = (): string => _userTimeZone;

/**
 * @function getUserTimeZoneOffset
 *
 * Returns the application-wide Time Zone utcOffset as specified via a call to the
 * setUserTimeZone function.
 *
 * ```typescript
 *   import {getUserTimeZoneOffset} from '@ion/core/src/timezone';
 *
 *   const timeZoneOffset: number = getUserTimeZoneOffset();
 *
 * ```
 *
 * @returns {number} The authenticated User's Time Zone utcOffset.
 */
export const getUserTimeZoneOffset = (): number => moment.tz(_userTimeZone).utcOffset();

/**
 * @function setUserTimeZone
 *
 * Provides the entry point into the Time Zone API, which allows for specifying
 * the Time Zone to which conversions to/from the CTC Services' Time Zone are
 * to be made. If the specified Time Zone value is undefined/null/empty, the
 * current Time Zone value (default: browser Time Zone) is retained. This
 * function should be invoked upon successful receipt of the authenticated
 * User entity from CTC Services.
 *
 * ```typescript
 *   import {setUserTimeZone} from '@ion/core/src/timezone/timezone';
 *   import {info} from '@ion/core/src/logger/logger';
 *   import {User} from '@ion/core/src/types/entities';
 *
 *   // retrieve authenticated user and set time zone ...
 *   const user: User = await currentUser();
 *   setUserTimeZone(user.timeZone);
 *   info(`User/Application Time Zone: [${getUserTimeZone()}]`);
 *   info(user.timeZone === getUserTimeZone()); // true
 *
 * ```
 *
 * @param {string} timeZone The authenticated User's Time Zone.
 */
export const setUserTimeZone = (timeZone?: string): void => {
  _userTimeZone = timeZone || _userTimeZone;
  moment.tz.setDefault(_userTimeZone);
  info(`User Time Zone: ${_userTimeZone}`);
};

/**
 * @function getPlatformTimeZone
 *
 * Returns the CTC Services' Time Zone.
 *
 * ```typescript
 *   import {getPlatformTimeZone} from '@ion/core/src/timezone';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   const timeZone: string = getPlatformTimeZone();
 *   info(`CTC Services Time Zone: [${timeZone}]`);
 *
 * ```
 *
 * @returns {string} The CTC Services' Time Zone.
 */
export const getPlatformTimeZone = (): string => _platformTimeZone;

/**
 * @function convertTime
 *
 * Given a timestamp value and a Time Zone to which the value is to be
 * converted, returns the converted timestamp value.
 *
 * ```typescript
 *   import {convertTime} from '@ion/core/src/timezone';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   // convert timestamp from user's timezone (ex. PST) to UTC ...
 *   info(convertTime(
 *     '1/1/2021 12:30pm',
 *     getPlatformTimeZone()
 *   )); // '2021-01-01T20:30:00Z'
 *
 *   // convert timestamp from UTC to user's timezone (ex. PST) ...
 *   info(convertTime(
 *     '2021-01-01T12:34:56Z',
 *     getUserTimeZone()
 *   )); // '1/1/2021 12:34pm'
 *
 * ```
 *
 * @param   {DateType} val The timestamp value which is to be converted.
 * @param   {string} to The Time Zone which the timestamp value is to be
 *          converted to.
 * @param   {string} format An optional format which, when specified, will
 *          return the converted timestamp value as a formatted string.
 * @returns {DateType} The converted timestamp value.
 */
export const convertTime = (val: DateType, to: string, format?: string): DateType => {
  return val ? toTimeZone(val, to, format) : '';
};

// Convenience wrapper functions.
export const convertToPlatformTime = (val: DateType, format?: string): DateType => {
  return convertTime(val, _platformTimeZone, format);
};

export const convertToUserTime = (val: DateType, format?: string): DateType => {
  return convertTime(val, _userTimeZone, format);
};
